import { useState } from 'react'

import {
  Box,
  BoxList,
  Button,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  AtsGetHiringTeamDocument,
  AtsRemoveHiringManagerDocument,
} from 'generated/resources/graphql'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AddHiringManagerModal } from './AddHiringManagerModal'
import { HiringManagerInfo } from './HiringManagerInfo'
import { HiringTeamSkeleton } from './HiringTeamSkeleton'

interface Props {
  jobId: number
  companyId: number
}

export const HiringTeam = ({ jobId, companyId }: Props) => {
  const [openDeleteHirinManagerTooltip, toggleDeleteHiringManagerTooltip] = useToggle()
  const [addHiringManagerOpen, setAddHiringManagerOpen] = useState(false)
  const [destroyAtsHiringManager] = useMutation(AtsRemoveHiringManagerDocument, {
    extractorFn: (data) => data.atsMutations.destroyAtsHiringManager,
  })

  const { data, loading } = useQuery(AtsGetHiringTeamDocument, {
    variables: {
      jobId,
      companyId: companyId,
    },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessHiringTeam)
  }, [])

  const hiringManagers = data?.ats.hiringManagersConnection.nodes
  const hiringManagerRolesFeature =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === 'ats_customized_features'
    )?.available || false
  const hiringManagerRolesEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_ats_hiring_manager_roles'
    )?.enabled || false

  const canManageAtsSettings = data?.ats._policies.manageAtsSettings.can ?? false

  const removeHiringManager = async (id: number) => {
    const result = await destroyAtsHiringManager({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ id, __typename: 'AtsHiringManager' }) })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  if (loading || !hiringManagers) {
    return <HiringTeamSkeleton />
  }

  const onlyOneHiringManager = hiringManagers.length === 1

  return (
    <>
      <Box gap="s8">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('ats.job_posting_settings.hiring_team.title')}
        </Text>
        <Text size="300" color="grey800">
          {i18n.t('ats.job_posting_settings.hiring_team.description')}
        </Text>
        <Box paddingTop="s8">
          <Button onClick={() => setAddHiringManagerOpen(true)} iconName="add" type="secondary">
            {i18n.t('common.add')}
          </Button>
        </Box>
      </Box>

      <Box paddingTop="s40" gap="s16">
        {!hiringManagers.length && (
          <Box background="grey000White" justifyContent="center" width="full" padding="s32">
            <Text.Normal weight="medium">
              {i18n.t('ats.job_posting_settings.hiring_team.not_found')}
            </Text.Normal>
          </Box>
        )}

        {hiringManagerRolesEnabled && (
          <HiringManagerInfo
            hiringManagers={hiringManagers}
            removeHiringManager={removeHiringManager}
            hasHiringManagerRolesFeature={hiringManagerRolesFeature}
            canManageAtsSettings={canManageAtsSettings}
          />
        )}

        {!hiringManagerRolesEnabled && (
          <BoxList>
            {hiringManagers.map((hm, index) => (
              <BoxList.Item key={index}>
                <Box
                  width="full"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  flexDirection="row"
                  flexGrow
                >
                  <Box alignItems="center" flexDirection="row" gap="s16">
                    {hm.access && <UserAvatar user={hm.access} />}
                    <Text.Normal weight="medium" color="grey1000" ellipsis>
                      {hm.access?.fullName}
                    </Text.Normal>
                  </Box>

                  {onlyOneHiringManager ? (
                    <Tooltip.Alt
                      open={openDeleteHirinManagerTooltip}
                      toggle={toggleDeleteHiringManagerTooltip}
                      label={i18n.t(
                        'ats.job_posting_settings.hiring_team.remove_hiring_manager_warning'
                      )}
                      attachment="top left"
                    >
                      <Button.Decorative
                        type="secondary"
                        iconName="delete"
                        aria-label={i18n.t(
                          'ats.job_posting_settings.hiring_team.remove_hiring_manager'
                        )}
                        disabled
                      />
                    </Tooltip.Alt>
                  ) : (
                    <Button
                      type="secondary"
                      onClick={() => removeHiringManager(hm.id)}
                      iconName="delete"
                      aria-label={i18n.t(
                        'ats.job_posting_settings.hiring_team.remove_hiring_manager'
                      )}
                    />
                  )}
                </Box>
              </BoxList.Item>
            ))}
          </BoxList>
        )}
      </Box>

      {addHiringManagerOpen && (
        <AddHiringManagerModal jobId={jobId} onClose={() => setAddHiringManagerOpen(false)} />
      )}
    </>
  )
}
